import styled, { CSSObject } from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { useContext, useEffect, useLayoutEffect, useState } from 'react'
import { IoChevronDownCircleOutline as ChevronIcon } from 'react-icons/io5'
import { IoMdHeart as HeartIcon } from 'react-icons/io'
import { PartnerStore, StoreRankReasons } from '../types'
import { formatPartnerAddressToString, handleScrollToStore } from './utils'
import OpeningHoursSection from './OpeningHoursSection'
import ExpandableDiv from 'shop/components/ExpandableContainer/ExpandableDiv'
import FulfillmentInfo from './FulfillmentInfo'
import { fadeIn } from 'shop/components/Shop/commonStyles'
import StoreTags from './StoreTags'
import { DietaryRequirements } from 'shop/components/ProductInfo'
import { LandingContext } from '../LandingContext'

type Props = {
  store: PartnerStore
  isDeliveryEnabled: boolean
  isPickupEnabled: boolean
  onLoadDateTime: Date
  distance?: number
  rankReasons: StoreRankReasons[]
  itemRefs: React.MutableRefObject<Record<string, HTMLLIElement | null>>
  inModal: boolean
}

const LandingStoreListItem = ({
  store,
  isDeliveryEnabled,
  isPickupEnabled,
  onLoadDateTime,
  distance,
  rankReasons,
  itemRefs,
  inModal
}: Props) => {
  const { mapInteracts, setMapInteracts } = useContext(LandingContext)

  const [lineClamp, setLineClamp] = useState<number>(2)
  const [hasLoaded, setHasLoaded] = useState<boolean>(false)
  const [descriptionClampedHeight, setDescriptionClampedHeight] = useState<
    number | null
  >(null)
  const [isStoreInfoExpanded, setIsStoreInfoExpanded] = useState<boolean>(false)

  const sortedRankReasons =
    rankReasons.length >= 2 ? rankReasons.slice().sort() : rankReasons

  const hasPreviouslyOrdered = !!sortedRankReasons.find(
    (rankReason) => rankReason === 'CUSTOMER_ORDERED_FROM_STORE'
  )

  useLayoutEffect(() => {
    // set height of clamped <p> so we can animate it later
    const descriptionEl = document.getElementById(`${store.slug}-description`)
    if (descriptionEl) {
      const descriptionElHeight = descriptionEl.clientHeight
      setDescriptionClampedHeight(descriptionElHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useLayoutEffect(() => {
    // delay lineClamp so we can animate the collapse of the text
    if (isStoreInfoExpanded) return
    const timeout = setTimeout(() => {
      setLineClamp(2)
    }, 500)

    return () => {
      clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStoreInfoExpanded])

  const handleExpandToggle = () => {
    // set lineClamp to 0 here before expand render
    if (!isStoreInfoExpanded) setLineClamp(0)
    setIsStoreInfoExpanded(!isStoreInfoExpanded)
    // set the expanded store to the "currently viewed store"
    if (!isStoreInfoExpanded) {
      setMapInteracts({ ...mapInteracts, currentlyViewedStore: store })
    } else {
      setMapInteracts({ ...mapInteracts, currentlyViewedStore: null })
    }
  }

  // Wait for component to load after fade in to not trigger other CSS transitions.
  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasLoaded(true)
    }, 500)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  // Map Interaction - Scroll to store and expand content on Marker click
  useEffect(() => {
    if (mapInteracts.currentlyViewedStore) {
      const { currentlyViewedStore } = mapInteracts

      if (currentlyViewedStore.slug === store.slug) {
        setIsStoreInfoExpanded(true)
        handleScrollToStore(itemRefs, currentlyViewedStore)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInteracts.currentlyViewedStore])

  return (
    <LandingStoreListItemContainer
      id={`store-list-item-${store.slug}`}
      ref={(el: HTMLLIElement | null) =>
        (itemRefs.current[`${store.slug}${inModal ? '-modal' : ''}`] = el)
      }
      onMouseEnter={() =>
        setMapInteracts({ ...mapInteracts, currentlyHoveredStore: store })
      }
      onMouseLeave={() =>
        setMapInteracts({ ...mapInteracts, currentlyHoveredStore: null })
      }
    >
      {hasPreviouslyOrdered && (
        <PreviouslyOrderedTag>
          <HeartIcon />
          <span>Previously ordered</span>
        </PreviouslyOrderedTag>
      )}
      <StoreContentBorder hasPreviouslyOrdered={hasPreviouslyOrdered}>
        <StoreContent>
          <StoreInfo>
            <HeaderContent>
              <LeftHeaderContent>
                <h3>
                  {store.name}
                  {!!store.address.zip && (
                    <span>&nbsp;&nbsp;{store.address.zip}</span>
                  )}
                </h3>
              </LeftHeaderContent>
              <RightHeaderContent isStoreInfoExpanded={isStoreInfoExpanded}>
                {distance && (
                  <DistanceContainer>
                    <div>{distance}</div>
                    <span>miles</span>
                  </DistanceContainer>
                )}
                <ChevronIcon
                  data-testid={`${store.slug}-expand-icon`}
                  onClick={handleExpandToggle}
                />
              </RightHeaderContent>
            </HeaderContent>
            <BodyContent>
              {!!store.description && (
                <ExpandableDiv
                  isExpanded={isStoreInfoExpanded}
                  collapsedHeight={
                    !!descriptionClampedHeight
                      ? descriptionClampedHeight
                      : 'content'
                  }
                  transition={hasLoaded ? undefined : 'none'}
                >
                  <Description
                    id={`${store.slug}-description`}
                    lineClamp={lineClamp}
                    isStoreInfoExpanded={isStoreInfoExpanded}
                    onClick={
                      !isStoreInfoExpanded ? handleExpandToggle : () => {}
                    }
                  >
                    {store.description}
                  </Description>
                </ExpandableDiv>
              )}
              <ExpandableDiv
                id={`${store.slug}-expandable-info`}
                isExpanded={isStoreInfoExpanded}
                collapsedHeight={0}
                transition={hasLoaded ? undefined : 'none'}
              >
                <ContentContainer
                  style={{
                    paddingTop: !!store.description ? '16px' : '8px'
                  }}
                >
                  <ContentItem>
                    <h4>Address</h4>
                    <p>{formatPartnerAddressToString(store.address)}</p>
                  </ContentItem>
                  <ContentItem>
                    <h4>Opening Hours</h4>
                    <OpeningHoursSection
                      openingHours={store.openingHours}
                      storeSlug={store.slug}
                    />
                  </ContentItem>
                </ContentContainer>
              </ExpandableDiv>
            </BodyContent>
            <div style={{ marginTop: '12px' }}>
              <DietaryRequirements
                dietaryRequirements={store.dietaryRequirements}
              />
            </div>
          </StoreInfo>
          <Divider />
          <StoreTags
            sortedRankReasons={sortedRankReasons}
            isDeliveryEnabled={isDeliveryEnabled}
            isPickupEnabled={isPickupEnabled}
          />
          <FulfillmentInfo
            store={store}
            onLoadDateTime={onLoadDateTime}
            isDeliveryEnabled={isDeliveryEnabled}
            isPickupEnabled={isPickupEnabled}
            sortedRankReasons={sortedRankReasons}
          />
        </StoreContent>
      </StoreContentBorder>
    </LandingStoreListItemContainer>
  )
}

export const LandingStoreListItemContainer = styled.li<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    boxShadow: `0px 3px 8px -4px rgba(0, 0, 0, 0.12)`
  })
)

export const StoreContentBorder = styled.div<
  StyledHTMLElement & { hasPreviouslyOrdered: boolean }
>(({ hasPreviouslyOrdered }) => ({
  display: 'flex',
  flex: 1,
  backgroundColor: '#F5F5F5',
  border: '1px solid #D9D9D9',
  borderRadius: hasPreviouslyOrdered ? '0px 8px 8px 8px' : '8px',
  padding: hasPreviouslyOrdered ? '4px' : '0px'
}))

export const StoreContent = styled.div<StyledHTMLElement>(
  ({ theme }): CSSObject => ({
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '16px',
    padding: '16px 12px',
    backgroundColor: '#fff',
    borderRadius: theme.borderRadius,
    animation: `${fadeIn} 0.3s ease-in-out forwards`
  })
)

const HeaderContent = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '12px'
}))

const LeftHeaderContent = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignSelf: 'center',

    // store name
    '>h3': {
      fontSize: '18px',
      lineHeight: '21px',

      // postcode
      '>span': {
        fontSize: '16px',
        color: '#8B8B8B',
        lineHeight: '19px',
        fontWeight: 600
      }
    }
  })
)

const RightHeaderContent = styled.div<
  StyledHTMLElement & { isStoreInfoExpanded: boolean }
>(({ isStoreInfoExpanded }) => ({
  display: 'flex',
  gap: '4px',

  '>svg': {
    minHeight: '24px',
    minWidth: '24px',
    cursor: 'pointer',
    rotate: isStoreInfoExpanded ? '180deg' : '0deg',
    transition: 'all 500ms ease-in-out',
    // disable blue hightlight of user clicks
    WebkitTapHighlightColor: 'transparent',

    '&:hover': {
      opacity: 0.8
    },
    '&:active': {
      opacity: 0.6
    }
  }
}))

const Description = styled.p<
  StyledHTMLElement & { isStoreInfoExpanded: boolean; lineClamp: number }
>(({ theme, isStoreInfoExpanded, lineClamp }) => ({
  fontSize: '14px',
  padding: '0',
  paddingTop: '8px',
  margin: '0',
  color: '#59595A',
  cursor: isStoreInfoExpanded ? 'inherit' : 'pointer',
  // set max lines to 2
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: lineClamp
}))

const StoreInfo = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column'
  })
)

const Divider = styled.hr(() => ({
  width: '100%',
  margin: 0,
  border: 0,
  borderBottom: `1px solid #EFEFF0`,
  borderRadius: '1px'
}))

const BodyContent = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column'
  })
)

const ContentContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  })
)

const ContentItem = styled.div<StyledHTMLElement>(({ theme }) => ({
  '>h4': {
    fontSize: '16px',
    margin: 0,
    marginBottom: '4px',
    padding: 0,
    color: '#2A2A2A'
  },

  '& p': {
    margin: 0,
    padding: 0,
    fontSize: '14px',
    color: '#59595A'
  }
}))

const DistanceContainer = styled.div<StyledHTMLElement>(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    fontSize: '16px',
    lineHeight: '16px',
    alignItems: 'center',
    fontWeight: 500,

    '& span': {
      lineHeight: '9px',
      fontSize: '9px'
    }
  })
)

const PreviouslyOrderedTag = styled.div<StyledHTMLElement>(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  maxWidth: 'fit-content',
  borderRadius: '6px 6px 0 0',
  border: '1px solid #D9D9D9',
  borderBottom: 'none',
  padding: '6px 12px',
  backgroundColor: '#F5F5F5',
  marginBottom: '-1px', // to cover the StoreContentBorder 1px border
  zIndex: 1,

  fontWeight: 500,
  fontSize: '14px',

  '& svg': {
    height: '16px',
    width: '16px'
  }
}))

export default LandingStoreListItem
